import type { KalenderColumn } from 'admination-react'
import { formatDatum } from './dates'

export type KalenderModus = 'woche' | 'monat'
export type Zeitraum = { von: string; bis: string }

const TAG_MS = 86_400_000
export const WOCHENTAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]
const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]

const tagIndex = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / TAG_MS
const isoVon = (index: number): string => new Date(index * TAG_MS).toISOString().slice(0, 10)
const wochentag = (iso: string): number => new Date(tagIndex(iso) * TAG_MS).getUTCDay()
const montagVon = (iso: string): number => tagIndex(iso) - (wochentag(iso) + 6) % 7

function kalenderwoche(iso: string): number {
  const donnerstag = montagVon(iso) + 3
  const jahresbeginn = Date.UTC(new Date(donnerstag * TAG_MS).getUTCFullYear(), 0, 1) / TAG_MS
  return Math.floor((donnerstag - jahresbeginn) / 7) + 1
}

export function zeitraumVon(modus: KalenderModus, anker: string): Zeitraum {
  if (modus === 'woche') {
    const montag = montagVon(anker)
    return { von: isoVon(montag), bis: isoVon(montag + 4) }
  }
  const [jahr, monat] = anker.split('-').map(Number)
  return { von: isoVon(Date.UTC(jahr, monat - 1, 1) / TAG_MS), bis: isoVon(Date.UTC(jahr, monat, 0) / TAG_MS) }
}

export function ankerVerschoben(modus: KalenderModus, anker: string, schritt: 1 | -1): string {
  if (modus === 'woche') return isoVon(tagIndex(anker) + 7 * schritt)
  const [jahr, monat] = anker.split('-').map(Number)
  return isoVon(Date.UTC(jahr, monat - 1 + schritt, 1) / TAG_MS)
}

export const arbeitstagVon = (iso: string): string => isoVon(Math.min(tagIndex(iso), montagVon(iso) + 4))

export function ankerBeiWechsel(ziel: KalenderModus, anker: string, heute: string): string {
  if (ziel === 'monat') return arbeitstagVon(anker)
  const monat = anker.slice(0, 7)
  const aktuell = arbeitstagVon(heute)
  if (aktuell.startsWith(monat)) return aktuell
  const erster = `${monat}-01`
  return isoVon(tagIndex(erster) + (8 - wochentag(erster)) % 7)
}

export function zeitraumLabel(modus: KalenderModus, { von, bis }: Zeitraum): string {
  if (modus === 'woche') return `KW ${kalenderwoche(von)} · ${formatDatum(von).slice(0, 6)} – ${formatDatum(bis)}`
  return `${MONATE[Number(von.slice(5, 7)) - 1]} ${von.slice(0, 4)}`
}

export function kalenderSpalten(modus: KalenderModus, { von, bis }: Zeitraum): KalenderColumn[] {
  const spalten: KalenderColumn[] = []
  for (let index = tagIndex(von); index <= tagIndex(bis); index += 1) {
    const iso = isoVon(index)
    const tag = wochentag(iso)
    if (tag === 0 || tag === 6) continue
    spalten.push(modus === 'woche'
      ? { key: iso, label: `${WOCHENTAGE[tag]} ${formatDatum(iso).slice(0, 6)}` }
      : { key: iso, label: String(Number(iso.slice(8))), weekday: WOCHENTAGE[tag], group: `KW ${kalenderwoche(iso)}` })
  }
  return spalten
}
